import { useEffect } from 'react';
import { useAuthStore } from '@/entities/session/auth-store';
import { isGuardian } from '@/entities/session/persona';
import { rememberKids, getKnownKids, clearKnownKids } from '@/entities/session/known-kids';
import { useMembersGuarded } from '@/entities/member/use-members-guarded';

/**
 * On a guardian session, cache the household's children to this device so the kid-login picker can
 * offer "Who's playing?" without ever exposing the member list pre-auth (see `known-kids.ts`). The
 * query is disabled for child sessions (a child sees only itself, which must not overwrite the
 * picker). Mounted once in the authenticated shell.
 */
export function useRememberKids(): void {
  const user = useAuthStore((s) => s.user);
  // An impersonation session (ADR-0055) presents as kind:'parent' scoped to the TARGET household, but
  // it is an operator device, not a family device: it must never seed (or wipe) this device's
  // kid-picker roster with another household's children.
  const guardian = !!user && isGuardian(user) && user.impersonatedHouseholdId == null;
  const members = useMembersGuarded({ enabled: guardian });

  // Safety net INDEPENDENT of member:read: a guardian signing in for a DIFFERENT household than the cached
  // roster must never leave the previous household's children in the "Who's playing?" picker. The overwrite
  // effect below only runs with member:read (members.data), so a custom role that omits it would otherwise
  // let a stale cross-household roster (names + avatars) survive on a shared device indefinitely.
  useEffect(() => {
    if (!guardian || !user || user.householdId === null) return;
    const cached = getKnownKids();
    if (cached && cached.householdId !== user.householdId) {
      clearKnownKids();
    }
  }, [guardian, user]);

  useEffect(() => {
    if (!guardian || !user || !members.data) return;
    const kids = members.data.data
      .filter((m) => m.kind === 'child')
      // Pre-auth picker shows the PRESET glyph only — an uploaded photo is auth-gated (ADR-0037) and
      // never cached/rendered before sign-in, so we persist just the preset key (or undefined → the
      // default glyph for a child whose avatar is currently an uploaded photo).
      .map((m) => ({
        id: m.id,
        displayName: m.displayName,
        avatar: m.avatar.kind === 'preset' ? m.avatar.key : undefined,
      }));
    if (user.householdId !== null) {
      rememberKids({ householdId: user.householdId, kids });
    }
  }, [guardian, user, members.data]);
}
